import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import { baseLocale, type Locale } from '@bells/i18n/runtime';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const hoursAndDaysStrings = {
  sameDay: 'HH:mm',
  lastDay: 'DD.MM.YYYY HH:mm',
  lastWeek: 'DD.MM.YYYY HH:mm',
  sameElse: 'DD.MM.YYYY HH:mm',
};

interface CalendarFormatProps {
  date?: Date | string | null;
  locale?: Locale;
}

export const CalendarFormat: React.FC<CalendarFormatProps> = React.memo(
  ({ date, locale = baseLocale }) => {
    const { formattedDate, datetimeAttr, iso } = useMemo(() => {
      if (!date) return { formattedDate: null, datetimeAttr: undefined };

      const now = dayjs().utc();
      const d = dayjs(date).utc().locale(locale);

      let formatStr = hoursAndDaysStrings.sameElse;

      if (d.isSame(now, 'day')) {
        formatStr = hoursAndDaysStrings.sameDay;
      } else if (d.isSame(now.subtract(1, 'day'), 'day')) {
        formatStr = hoursAndDaysStrings.lastDay;
      } else if (d.isAfter(now.subtract(7, 'day'))) {
        formatStr = hoursAndDaysStrings.lastWeek;
      }

      const datetime = d.valueOf().toString();

      return {
        formattedDate: d.format(formatStr),
        iso: d.toISOString(),
        datetimeAttr: datetime,
      };
    }, [date, locale]);

    if (formattedDate === null) {
      return null;
    }

    return <time dateTime={iso}>{formattedDate}</time>;
  }
);
